import { Draggable, Droppable, getDroppablePayload } from "@msuite/ui/dnd";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import type { Tables } from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import { mutators, type ZeroVehicleAssignmentPlan } from "@msuite/zero";
import { useZero } from "@msuite/zero/react";
import { syncHandler } from "@/api/sync-handler";
import { UserCard } from "@/components/cards";
import { useDailyContext } from "./context";

/** Props Interface */
interface DailyVehicleAssignmentPlanProps {
	vehicleAssignmentPlan: Readonly<ZeroVehicleAssignmentPlan>;
}

export function DailyVehicleAssignmentPlan({
	vehicleAssignmentPlan,
}: DailyVehicleAssignmentPlanProps) {
	const z = useZero();
	const { t } = useTranslation();
	const { isInteractionBlocked } = useDailyContext();

	/** Render */
	return (
		<Draggable
			type="vehicle-assignment-plan"
			item={vehicleAssignmentPlan}
			isDisabled={isInteractionBlocked}
		>
			<Droppable
				accept={["user", "user-assignment", "user-vehicle-assignment"]}
				key={vehicleAssignmentPlan.id}
				className="v-stack card gap-0 py-1 items-center justify-between items-start"
				isDisabled={isInteractionBlocked}
				handleOnDrop={(e) => {
					const payload = getDroppablePayload(e);
					if (payload.type === "user-assignment") {
						const item = payload.item as Tables<"userAssignments">;
						syncHandler(
							z.mutate(
								mutators.vehicleAssignmentPlans.assignUser({
									vehicleAssignmentPlanId: vehicleAssignmentPlan.id,
									userId: item.userId,
								}),
							),
						);
					} else if (payload.type === "user") {
						const item = payload.item as Tables<"users">;
						syncHandler(
							z.mutate(
								mutators.vehicleAssignmentPlans.assignUser({
									vehicleAssignmentPlanId: vehicleAssignmentPlan.id,
									userId: item.id,
								}),
							),
						);
					} else if (payload.type === "user-vehicle-assignment") {
						const item = payload.item as Tables<"vehicleAssignmentsPlan">;
						const userId = item.userId;
						if (!userId) return;
						syncHandler(
							z.mutate(
								mutators.vehicleAssignmentPlans.assignUser({
									vehicleAssignmentPlanId: vehicleAssignmentPlan.id,
									userId,
								}),
							),
						);
					}
				}}
			>
				<div className="h-stack">
					<StatusIndicator color="accent" />
					<p>{vehicleAssignmentPlan.vehicle?.licensePlate}</p>
				</div>
				<div className="h-stack justify-end w-full grow-0">
					{vehicleAssignmentPlan.userId ? (
						<Draggable
							className="min-w-0 shrink w-full"
							type="user-vehicle-assignment"
							item={vehicleAssignmentPlan}
							isDisabled={isInteractionBlocked}
						>
							<UserCard
								userId={vehicleAssignmentPlan.userId}
								variant="plain"
								withAvatar={false}
								withPopover={false}
								render={(user) => {
									return <p className="truncate">{getFullName(user)}</p>;
								}}
							/>
						</Draggable>
					) : (
						<div className="h-stack opacity-50 text-sm w-full">
							<Icon name="UserCircle2" />
							<p>{t("planning.assign_user")}</p>
						</div>
					)}
				</div>
			</Droppable>
		</Draggable>
	);
}
